import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";

import * as echarts from "echarts";

import Loader from "../../Components/Common/Loader";
import { usePageApiQueue } from "../../Components/Hooks/PageApiQueue";
import { postDataAPI } from "../../helpers/phpbackend_helper";
import { POST_MENU_OPTIMIZATION } from "../../helpers/data_helper";
import {
  getDateByName,
  convertDateFormate,
  currencyFormatWithAlert,
} from "../../helpers/api_helper";

const CourseProductClassification = ({
  leftFilter,
  rightFilter,
  leftCustomDate,
  mealPeriod,
  currency,
  defaultCourse,
  comparisonDate,
  activeComparisonData,
  eventFilter,
  chartMax = 100,
}) => {
  const { enqueue } = usePageApiQueue();

  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const [chartData, setChartData] = useState({
    categories: [],
    courseIds: [],
    courseMap: {},
    series: {
      Star: [],
      Puzzle: [],
      "Cash Cow": [],
      Dog: [],
    },
    avgCheck: {
      Star: [],
      Puzzle: [],
      "Cash Cow": [],
      Dog: [],
    },
  });

  const [isLoading, setIsLoading] = useState(true);

  const [modal, setModal] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [modalAjax, setModalAjax] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);

  // ---------------------------------------------------------
  // Empty chart data
  // ---------------------------------------------------------
  const getEmptyChartData = useCallback(
    () => ({
      categories: [],
      courseIds: [],
      courseMap: {},
      series: {
        Star: [],
        Puzzle: [],
        "Cash Cow": [],
        Dog: [],
      },
      avgCheck: {
        Star: [],
        Puzzle: [],
        "Cash Cow": [],
        Dog: [],
      },
    }),
    []
  );

  // ---------------------------------------------------------
  // Map API response
  // ---------------------------------------------------------
  const mapCourseResponse = useCallback(
    (responseData) => {
      const courses = Array.isArray(responseData)
        ? responseData.map((course, index) => [course.id || index, course])
        : Object.entries(responseData || {});

      const mappedData = getEmptyChartData();

      courses.forEach(([courseId, course]) => {
        if (!course || typeof course !== "object") return;

        const id = String(courseId);
        const courseName =
          course.cfv_display_name || String(courseId);

        mappedData.courseIds.push(id);
        mappedData.categories.push(courseName);
        mappedData.courseMap[id] = courseName;

        const groups = course.performance_groups || [];

        const groupValues = {};
        const groupAvgCheck = {};

        groups.forEach((group) => {
          const performanceGroup = group.performance_group;

          groupValues[performanceGroup] =
            Number(group.item_sold) || 0;

          groupAvgCheck[performanceGroup] =
            Number(group.avg_check) || 0;
        });

        // -------------------------------
        // Item Sold
        // -------------------------------
        mappedData.series.Star.push(
          groupValues.Star || 0
        );

        mappedData.series.Puzzle.push(
          groupValues.Puzzle || 0
        );

        mappedData.series["Cash Cow"].push(
          groupValues["Cash Cow"] || 0
        );

        mappedData.series.Dog.push(
          groupValues.Dog || 0
        );

        // -------------------------------
        // Average Check
        // -------------------------------
        mappedData.avgCheck.Star.push(
          groupAvgCheck.Star || 0
        );

        mappedData.avgCheck.Puzzle.push(
          groupAvgCheck.Puzzle || 0
        );

        mappedData.avgCheck["Cash Cow"].push(
          groupAvgCheck["Cash Cow"] || 0
        );

        mappedData.avgCheck.Dog.push(
          groupAvgCheck.Dog || 0
        );
      });

      return mappedData;
    },
    [getEmptyChartData]
  );

  // ---------------------------------------------------------
  // Course name -> ID
  // ---------------------------------------------------------
  const courseNameToIdMap = useMemo(() => {
    const m = {};

    if (chartData.courseMap) {
      Object.entries(chartData.courseMap).forEach(([id, name]) => {
        m[name] = id;
      });
    }

    return m;
  }, [chartData.courseMap]);

  // ---------------------------------------------------------
  // Handle course click
  // ---------------------------------------------------------
  const handleDataPointClick = useCallback(
    (courseId) => {
      if (!courseId) return;

      setSelectedCourse(courseId);

      const leftFilterData =
        leftFilter.value === "Custom"
          ? {
              from: convertDateFormate(leftCustomDate[0]),
              to: convertDateFormate(leftCustomDate[1]),
            }
          : getDateByName(leftFilter.value);

      setModal(true);
      setModalAjax(true);

      postDataAPI({
        course: courseId,
        dateFilter: leftFilterData,
        event_filter: eventFilter,
        serviceName: "menu_data",
        op: "get_items_under_tribe",
      }).then((response) => {
        setModalAjax(false);

        if (response.status === "ok") {
          setModalData(response?.data || []);
        } else {
          console.log(response);
          setModalData([]);
        }
      });
    },
    [leftFilter, leftCustomDate, eventFilter]
  );

  // ---------------------------------------------------------
  // Calculate stacked totals
  // ---------------------------------------------------------
  const stackedTotals = useMemo(() => {
    return chartData.categories.map((_, index) => {
      return (
        Number(chartData.series.Dog[index] || 0) +
        Number(chartData.series["Cash Cow"][index] || 0) +
        Number(chartData.series.Puzzle[index] || 0) +
        Number(chartData.series.Star[index] || 0)
      );
    });
  }, [chartData]);

  const maxStackedTotal = Math.max(
    ...stackedTotals,
    chartMax,
    100
  );

  const yAxisMax =
    Math.ceil((maxStackedTotal * 1.1) / 100) * 100;

  // ---------------------------------------------------------
  // Average Check max
  // ---------------------------------------------------------
  const maxAvgCheck = useMemo(() => {
    const values = [
      ...(chartData.avgCheck.Dog || []),
      ...(chartData.avgCheck["Cash Cow"] || []),
      ...(chartData.avgCheck.Puzzle || []),
      ...(chartData.avgCheck.Star || []),
    ];

    const max = Math.max(...values, 0);

    if (max <= 10) {
      return 10;
    }

    if (max <= 25) {
      return 25;
    }

    if (max <= 50) {
      return 50;
    }

    return Math.ceil(max * 1.1);
  }, [chartData]);

  // ---------------------------------------------------------
  // Create / Update ECharts
  // ---------------------------------------------------------
  useEffect(() => {
    if (!chartRef.current) return;

    // Create chart instance only once
    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(
        chartRef.current
      );
    }

    const chart = chartInstanceRef.current;

    // -------------------------------------------------------
    // ECharts Options
    // -------------------------------------------------------
    const option = {
      animation: true,

      grid: {
        left: 65,
        right: 70,
        top: 30,
        bottom: 100,
        containLabel: true,
      },

      // -----------------------------------------------------
      // X AXIS
      // -----------------------------------------------------
      xAxis: {
        type: "category",
        data: chartData.categories || [],

        axisLabel: {
          color: "#6c757d",
          fontSize: 12,
          fontWeight: 500,
          fontFamily: "Poppins, sans-serif",

          // Keep labels straight
          rotate: 0,

          // Show every label
          interval: 0,

          // Prevent automatic hiding
          hideOverlap: false,

          // Don't truncate labels
          overflow: "break",

          // Maximum width for each label
          width: 100,

          // Allow multiple lines if the name is long
          lineHeight: 16,
        },

        axisLine: {
          lineStyle: {
            color: "#e9e9ef",
          },
        },

        axisTick: {
          show: true,
          lineStyle: {
            color: "#e9e9ef",
          },
        },
      },

      // -----------------------------------------------------
      // TWO Y AXES
      // -----------------------------------------------------
      yAxis: [
        {
          type: "value",
          name: "Items Sold",
          min: 0,
          max: yAxisMax,
          splitNumber: 5,

          nameTextStyle: {
            color: "#495057",
            fontSize: 13,
            fontWeight: 600,
            fontFamily: "Poppins, sans-serif",
          },

          axisLabel: {
            color: "#6c757d",
            fontSize: 12,
            fontWeight: 500,
            fontFamily: "Poppins, sans-serif",

            formatter: (value) => {
              return Math.round(value);
            },
          },

          axisLine: {
            show: true,
            lineStyle: {
              color: "#e9e9ef",
            },
          },

          axisTick: {
            show: true,
            lineStyle: {
              color: "#e9e9ef",
            },
          },

          splitLine: {
            show: true,
            lineStyle: {
              color: "#e9e9ef",
              type: "dashed",
            },
          },
        },

        // ---------------------------------------------------
        // RIGHT AXIS - AVG CHECK
        // ---------------------------------------------------
        {
          type: "value",
          name: "Avg Check",
          min: 0,
          max: maxAvgCheck,
          splitNumber: 5,

          nameTextStyle: {
            color: "#495057",
            fontSize: 13,
            fontWeight: 600,
            fontFamily: "Poppins, sans-serif",
          },

          axisLabel: {
            color: "#6c757d",
            fontSize: 12,
            fontWeight: 500,
            fontFamily: "Poppins, sans-serif",

            formatter: (value) => {
              return value.toFixed(2);
            },
          },

          axisLine: {
            show: true,
            lineStyle: {
              color: "#e9e9ef",
            },
          },

          axisTick: {
            show: true,
            lineStyle: {
              color: "#e9e9ef",
            },
          },

          splitLine: {
            show: false,
          },
        },
      ],

      // -----------------------------------------------------
      // TOOLTIP
      // -----------------------------------------------------
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },

        backgroundColor: "#ffffff",
        borderColor: "#e9e9ef",
        borderWidth: 1,

        textStyle: {
          color: "#495057",
          fontSize: 12,
          fontFamily: "Poppins, sans-serif",
        },

        formatter: function (params) {
          if (!params || params.length === 0) {
            return "";
          }

          const category =
            params[0]?.axisValue || "";

          let html = `
            <div style="
              font-family: Poppins, sans-serif;
              min-width: 180px;
            ">
              <div style="
                font-weight: 600;
                margin-bottom: 8px;
                color: #343a40;
              ">
                ${category}
              </div>
          `;

          const barParams = params.filter(
            (item) => item.seriesType === "bar"
          );

          const lineParams = params.filter(
            (item) => item.seriesType === "line"
          );

          // -------------------------------
          // ITEM SOLD
          // -------------------------------
          if (barParams.length > 0) {
            html += `
              <div style="
                font-weight: 600;
                margin-bottom: 4px;
              ">
                Item Sold
              </div>
            `;

            barParams.forEach((item) => {
              html += `
                <div style="
                  display:flex;
                  justify-content:space-between;
                  gap:20px;
                  margin:3px 0;
                ">
                  <span>
                    ${item.marker}
                    ${item.seriesName}
                  </span>
                  <strong>
                    ${Number(item.value || 0).toLocaleString()}
                  </strong>
                </div>
              `;
            });

            const total = barParams.reduce(
              (sum, item) =>
                sum + Number(item.value || 0),
              0
            );

            html += `
              <div style="
                border-top:1px solid #eee;
                margin-top:5px;
                padding-top:5px;
                display:flex;
                justify-content:space-between;
              ">
                <strong>Total</strong>
                <strong>
                  ${total.toLocaleString()}
                </strong>
              </div>
            `;
          }

          // -------------------------------
          // AVG CHECK
          // -------------------------------
          if (lineParams.length > 0) {
            html += `
              <div style="
                font-weight: 600;
                margin-top:10px;
                margin-bottom:4px;
              ">
                Avg Check
              </div>
            `;

            lineParams.forEach((item) => {
              html += `
                <div style="
                  display:flex;
                  justify-content:space-between;
                  gap:20px;
                  margin:3px 0;
                ">
                  <span>
                    ${item.marker}
                    ${item.seriesName}
                  </span>
                  <strong>
                    ${Number(item.value || 0).toFixed(2)}
                  </strong>
                </div>
              `;
            });
          }

          html += `</div>`;

          return html;
        },
      },

      // -----------------------------------------------------
      // LEGEND
      // -----------------------------------------------------
      legend: {
        show: true,
        bottom: 10,
        left: "center",

        itemWidth: 14,
        itemHeight: 14,
        itemGap: 18,

        textStyle: {
          color: "#495057",
          fontSize: 13,
          fontWeight: 500,
          fontFamily: "Poppins, sans-serif",
        },
      },

      // -----------------------------------------------------
      // SERIES
      // -----------------------------------------------------
      series: [
        // ================================================
        // STACKED BAR - DOG
        // ================================================
        {
          name: "Dog",
          type: "bar",
          stack: "total",
          yAxisIndex: 0,

          data: chartData.series.Dog || [],

          barMaxWidth: 80,

          itemStyle: {
            color: "#F06548",
            borderColor: "rgba(0,0,0,0.1)",
            borderWidth: 1,
          },

          emphasis: {
            focus: "series",
          },

          z: 1,
        },

        // ================================================
        // STACKED BAR - CASH COW
        // ================================================
        {
          name: "Cash Cow",
          type: "bar",
          stack: "total",
          yAxisIndex: 0,

          data:
            chartData.series["Cash Cow"] || [],

          barMaxWidth: 55,

          itemStyle: {
            color: "#F7B84B",
            borderColor: "rgba(0,0,0,0.1)",
            borderWidth: 1,
          },

          emphasis: {
            focus: "series",
          },

          z: 1,
        },

        // ================================================
        // STACKED BAR - PUZZLE
        // ================================================
        {
          name: "Puzzle",
          type: "bar",
          stack: "total",
          yAxisIndex: 0,

          data: chartData.series.Puzzle || [],

          barMaxWidth: 55,

          itemStyle: {
            color: "#6C757D",
            borderColor: "rgba(0,0,0,0.1)",
            borderWidth: 1,
          },

          emphasis: {
            focus: "series",
          },

          z: 1,
        },

        // ================================================
        // STACKED BAR - STAR
        // ================================================
        {
          name: "Star",
          type: "bar",
          stack: "total",
          yAxisIndex: 0,

          data: chartData.series.Star || [],

          barMaxWidth: 55,

          itemStyle: {
            color: "#0AB39C",
            borderColor: "rgba(0,0,0,0.1)",
            borderWidth: 1,
          },

          emphasis: {
            focus: "series",
          },

          z: 1,
        },

        // ==================================================
        // AVG CHECK LINE - DOG
        // ==================================================
        {
          name: "Dog Avg Check",
          type: "line",
          yAxisIndex: 1,

          data: chartData.avgCheck.Dog || [],

          smooth: false,

          symbol: "circle",
          symbolSize: 7,

          lineStyle: {
            width: 3,
            color: "#C62828",
          },

          itemStyle: {
            color: "#C62828",
            borderColor: "#ffffff",
            borderWidth: 2,
          },

          emphasis: {
            focus: "series",
            scale: true,
          },

          z: 10,
        },

        // ==================================================
        // AVG CHECK LINE - CASH COW
        // ==================================================
        {
          name: "Cash Cow Avg Check",
          type: "line",
          yAxisIndex: 1,

          data:
            chartData.avgCheck["Cash Cow"] || [],

          smooth: false,

          symbol: "circle",
          symbolSize: 7,

          lineStyle: {
            width: 3,
            color: "#E09A00",
          },

          itemStyle: {
            color: "#E09A00",
            borderColor: "#ffffff",
            borderWidth: 2,
          },

          emphasis: {
            focus: "series",
            scale: true,
          },

          z: 10,
        },

        // ==================================================
        // AVG CHECK LINE - PUZZLE
        // ==================================================
        {
          name: "Puzzle Avg Check",
          type: "line",
          yAxisIndex: 1,

          data: chartData.avgCheck.Puzzle || [],

          smooth: false,

          symbol: "circle",
          symbolSize: 7,

          lineStyle: {
            width: 3,
            color: "#6C757D", // Grey
          },

          itemStyle: {
            color: "#6C757D", // Grey
            borderColor: "#ffffff",
            borderWidth: 2,
          },

          emphasis: {
            focus: "series",
            scale: true,
          },

          z: 10,
        },

        // ==================================================
        // AVG CHECK LINE - STAR
        // ==================================================
        {
          name: "Star Avg Check",
          type: "line",
          yAxisIndex: 1,

          data:
            chartData.avgCheck.Star || [],

          smooth: false,

          symbol: "circle",
          symbolSize: 7,

          lineStyle: {
            width: 3,
            color: "#00796B",
          },

          itemStyle: {
            color: "#00796B",
            borderColor: "#ffffff",
            borderWidth: 2,
          },

          emphasis: {
            focus: "series",
            scale: true,
          },

          z: 10,
        },
      ],
    };

    chart.setOption(option, true);

    // -------------------------------------------------------
    // Click event
    // -------------------------------------------------------
    chart.off("click");

    chart.on("click", function (params) {
      if (!params) return;

      const dataIndex = params.dataIndex;

      if (
        dataIndex === undefined ||
        dataIndex === null ||
        dataIndex < 0
      ) {
        return;
      }

      const courseName =
        chartData.categories[dataIndex];

      const courseId =
        courseNameToIdMap[courseName] ||
        chartData.courseIds?.[dataIndex];

      if (courseId) {
        handleDataPointClick(courseId);
      }
    });

    // -------------------------------------------------------
    // Resize
    // -------------------------------------------------------
    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener("resize", handleResize);

    // -------------------------------------------------------
    // Cleanup
    // -------------------------------------------------------
    return () => {
      window.removeEventListener(
        "resize",
        handleResize
      );

      chart.off("click");
    };
  }, [
    chartData,
    yAxisMax,
    maxAvgCheck,
    courseNameToIdMap,
    handleDataPointClick,
  ]);

  // ---------------------------------------------------------
  // Dispose chart
  // ---------------------------------------------------------
  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  // ---------------------------------------------------------
  // Fetch data
  // ---------------------------------------------------------
  useEffect(() => {
    if (
      (leftFilter.value !== "Custom" ||
        (leftFilter.value === "Custom" &&
          leftCustomDate[0] &&
          leftCustomDate[1])) &&
      (rightFilter.value !== "Custom" ||
        (rightFilter.value === "Custom" &&
          comparisonDate[0] &&
          comparisonDate[1]))
    ) {
      const leftFilterData =
        leftFilter.value === "Custom"
          ? {
              from: convertDateFormate(
                leftCustomDate[0]
              ),
              to: convertDateFormate(
                leftCustomDate[1]
              ),
            }
          : getDateByName(leftFilter.value);

      const rightFilterData =
        activeComparisonData &&
        activeComparisonData.datepiker
          ? {
              from: convertDateFormate(
                comparisonDate[0]
              ),
              to: convertDateFormate(
                comparisonDate[1]
              ),
            }
          : getDateByName(rightFilter.value);

      setIsLoading(true);

      return enqueue(
        "CourseProductClassification",
        2,
        async (signal) => {
          try {
            const response = await postDataAPI(
              {
                leftFilter: leftFilterData,
                rightFilter: rightFilterData,
                mealPeriod: mealPeriod,
                course: defaultCourse,
                event_filter: eventFilter,
                serviceName:
                  POST_MENU_OPTIMIZATION.serviceName,
                op: "course_wise_sold_performance_group",
              },
              signal
            );

            if (signal.aborted) return;

            setIsLoading(false);

            if (response.status === "ok") {
              const data = response.data;

              console.log(
                "CourseProductClassification API Response:",
                data
              );

              setChartData(
                mapCourseResponse(data)
              );
            } else {
              console.log(response);

              setChartData(
                getEmptyChartData()
              );
            }
          } catch (e) {
            if (!signal.aborted) {
              console.error(e);
              setIsLoading(false);
            }
          }
        }
      );
    }
  }, [
    leftFilter,
    rightFilter,
    mealPeriod,
    comparisonDate,
    activeComparisonData,
    leftCustomDate,
    defaultCourse,
    eventFilter,
    enqueue,
    mapCourseResponse,
    getEmptyChartData,
  ]);

  // ---------------------------------------------------------
  // Render
  // ---------------------------------------------------------
  return (
    <>
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="d-flex align-items-start">
            <div
              className={`icon-box rounded bg-danger text-white`}
            >
              <i className="ri-bar-chart-fill"></i>
            </div>

            <div className="ps-2">
              <h4 className="card-title mb-1">
                Courses Analysis
              </h4>

              <p className="text-muted mb-0">
                Item classification by course.
              </p>
            </div>
          </div>
        </CardHeader>

        <CardBody className="px-3 pb-3 pt-2">
          {isLoading && (
            <div
              style={{
                minHeight: "350px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Loader />
            </div>
          )}

          <div
            id="CourseProductClassification"
            className={
              isLoading ? "opacity-0" : ""
            }
            style={{
              width: "100%",
              height: "400px",
            }}
          >
            <div
              ref={chartRef}
              style={{
                width: "100%",
                height: "100%",
              }}
            />
          </div>
        </CardBody>
      </Card>

      {/* =====================================================
          MODAL FOR ITEM DETAILS
      ====================================================== */}

      <Modal
        className="modal-dialog modal-xl custom-bg"
        isOpen={modal}
        toggle={() => setModal(false)}
        id="fullscreeexampleModal"
        size="xl"
      >
        <ModalHeader
          className="modal-title"
          id="exampleModalFullscreenLabel"
          toggle={() => setModal(false)}
        >
          {modalData[0]?.cfv_display_name ||
            "Course Details"}
        </ModalHeader>

        <ModalBody>
          <Card
            style={{
              borderRadius: "8px",
              padding: "15px",
            }}
          >
            <CardBody>
              {modalAjax && <Loader />}

              {!modalAjax &&
                modalData?.length > 0 && (
                  <div
                    className="table-responsive table-card"
                    style={{
                      maxHeight: "500px",
                      overflow: "auto",
                    }}
                  >
                    <table className="table table-borderless table-centered align-middle table-nowrap mb-3 text-center">
                      <thead className="text-muted table-light">
                        <tr
                          style={{
                            position: "sticky",
                            top: -2,
                            zIndex: 100,
                          }}
                        >
                          <th
                            scope="col"
                            className="text-start"
                          >
                            Item Name
                          </th>

                          <th scope="col">
                            Performance Group
                          </th>

                          <th scope="col">
                            Item Sold
                          </th>

                          <th scope="col">
                            Avg Check
                          </th>

                          <th scope="col">
                            Revenue
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {modalData?.map(
                          (item, index) => (
                            <tr key={index}>
                              <td className="text-start">
                                <b>
                                  {item.item_name}
                                </b>
                              </td>

                              <td>
                                <span
                                  className={`badge bg-${
                                    item.performance_group ===
                                    "Plowhorse"
                                      ? "warning"
                                      : item.performance_group ===
                                        "Dog"
                                      ? "danger"
                                      : item.performance_group ===
                                        "Cash Cow"
                                      ? "warning"
                                      : item.performance_group ===
                                        "Puzzle"
                                      ? "light text-dark"
                                      : "success"
                                  }`}
                                >
                                  {
                                    item.performance_group
                                  }
                                </span>
                              </td>

                              <td>
                                {item.item_sold}
                              </td>

                              <td>
                                {currencyFormatWithAlert(
                                  item.item_sold
                                    ? item.total_sales /
                                        item.item_sold
                                    : 0,
                                  currency
                                )}
                              </td>

                              <td>
                                {currencyFormatWithAlert(
                                  item.total_sales,
                                  currency
                                )}
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

              {!modalAjax &&
                modalData?.length === 0 && (
                  <p className="text-center text-muted py-4">
                    No data available
                  </p>
                )}
            </CardBody>
          </Card>
        </ModalBody>
      </Modal>
    </>
  );
};

export default CourseProductClassification;